import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";

/** Keeps administration drill-downs separate from table and bulk actions. */
export function AdminListLayout({ filters, children }: { filters: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div data-testid="admin-list-layout" className="grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]">
      {filters}
      <div data-testid="admin-list-content" className="min-w-0 space-y-4">{children}</div>
    </div>
  );
}

export function AdminFilterPanel({ count, onClear, children }: { count: number; onClear: () => void; children: ReactNode }): JSX.Element {
  return (
    <aside aria-label="List filters" className="min-w-0 rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Filters <span className="text-muted-foreground">({count})</span></h2>
        <Button size="sm" variant="ghost" disabled={count === 0} onClick={onClear}>Clear all</Button>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">{children}</div>
    </aside>
  );
}

export function AdminFilterGroup({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</legend>
      {children}
    </fieldset>
  );
}

export function AdminFilterDisclosure({ label, count, children }: { label: string; count: number; children: ReactNode }): JSX.Element {
  return (
    <details className="min-w-0 rounded-md border border-border px-3 py-2">
      <summary className="cursor-pointer text-sm font-medium">
        {label}{count > 0 ? <span className="ml-1 text-muted-foreground">({count} active)</span> : null}
      </summary>
      <div className="mt-4 grid min-w-0 grid-cols-1 gap-4">{children}</div>
    </details>
  );
}
